'use client';

import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Typography, useTheme } from '@mui/material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { CSSProperties, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { GuaranteeIcon } from '@/components/Icons/GuaranteeIcon';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import {
  ComponentBlocksCta,
  Enum_Componentblockscta_Ctaheromediadisplay,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

export function Cta({ blocks }: { blocks: ComponentBlocksCta }) {
  const theme = useTheme();
  const { siteSettings } = useSiteSettings();
  const [videoError, setVideoError] = useState(false);
  const pathname = usePathname();

  const ctaHref = useMemo(() => {
    const currentPageCode = getPageCodeFromPath(pathname);
    // Prefer the block's own CtaUrl, falling back to the site-wide GetStartedUrl,
    // and always decorate with UTM params so the lead traces back to this page.
    const baseUrl = blocks.CtaUrl || siteSettings?.GetStartedUrl;
    if (!baseUrl) return '#';
    return buildUrlWithUtmParams(baseUrl, currentPageCode);
  }, [siteSettings?.GetStartedUrl, blocks.CtaUrl, pathname]);

  const ctaHeroMediaUrl = getImageUrl({
    image: blocks?.CtaHeroMedia,
    size: 'url',
  });
  const mediaElement = useMemo(() => {
    if (!ctaHeroMediaUrl) return null;

    const mediaBoxStyles = {
      position: 'relative' as const,
      width: '100%',
      height: { xs: '300px', sm: '400px', md: '500px' },
      borderRadius: '1.5rem',
      overflow: 'hidden',
    };

    const mediaStyles = {
      width: '100%',
      height: '100%',
      objectFit:
        blocks?.CTAHeroMediaDisplay === Enum_Componentblockscta_Ctaheromediadisplay.Contain
          ? 'contain'
          : ('cover' as const),
      objectPosition: 'top',
      borderRadius: '1.5rem',
    } as CSSProperties;

    if (
      blocks?.CtaHeroMedia &&
      blocks.CtaHeroMedia.mime?.startsWith('video/') &&
      ctaHeroMediaUrl &&
      !videoError
    ) {
      return (
        <Box sx={mediaBoxStyles}>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={mediaStyles}
            onError={() => setVideoError(true)}
          >
            <source src={ctaHeroMediaUrl} type={blocks.CtaHeroMedia.mime} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    return (
      <Box sx={mediaBoxStyles}>
        <Image
          src={ctaHeroMediaUrl}
          alt={blocks?.CtaHeroMedia?.alternativeText || 'Landing page hero media'}
          width={blocks?.CtaHeroMedia?.formats?.large?.width ?? 400}
          height={blocks?.CtaHeroMedia?.formats?.large?.height ?? 300}
          priority
          style={mediaStyles}
        />
      </Box>
    );
  }, [blocks?.CTAHeroMediaDisplay, blocks.CtaHeroMedia, ctaHeroMediaUrl, videoError]);

  const alignment = blocks.CTAAlignment || 'left';
  const alignmentStyles = {
    left: { textAlign: 'left', alignItems: 'flex-start' },
    center: { textAlign: 'center', alignItems: 'center' },
    right: { textAlign: 'right', alignItems: 'flex-end' },
  }[alignment];

  return (
    <CustomBackground
      backgroundOptions={(blocks?.CtaBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...blocks?.CtaDisplayOptions,
          ContentsWidth:
            blocks?.CtaDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: blocks?.CtaDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: blocks?.CtaDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: blocks?.CtaDisplayOptions?.PaddingInline ?? 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        sx: {
          textAlign: alignment,
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 4,
          rowGap: 0,
          px: 2,
          gridTemplateColumns: '1fr',
          '&.with-hero-media': {
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr auto',
            },
          },
        }}
        className={`${ctaHeroMediaUrl ? 'with-hero-media' : ''} ${
          blocks?.HideInnerBackground === true ? '' : 'inner-background'
        }`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            ...alignmentStyles,
            '.inner-background &': {
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: { xs: 25, md: 50 },
            },
            py: 2,
            '.with-hero-media &': {
              mx: { xs: 'auto', sm: '0' },
              pb: { xs: 0, md: 2 },
            },
          }}
        >
          <GlobalBlockRenderer
            content={blocks.Body || []}
            overrides={{
              blocks: {
                link: ({ url, children }: { url: string; children?: React.ReactNode }) => (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.brand.primary,
                      textDecoration: 'underline',
                    }}
                  >
                    {children}
                  </a>
                ),
              },
            }}
          />

          <Button
            variant="flat"
            color="primary"
            href={ctaHref}
            endIcon={<ArrowForward />}
            className={`button-${blocks?.CTAButtonStyle}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: theme.typography.medium.fontSize,
              px: 6,
              mb: { xs: 0, sm: 4 },
              textAlign: alignment,
              minWidth: '15rem',
              '&.button-Gradient': {
                background: theme.palette.gradients.gradientCta,
                color: theme.palette.common.white,
                '&:hover,&:focus': {
                  background: theme.palette.gradients.gradient1,
                },
              },
            }}
          >
            {blocks.CtaText}
          </Button>

          {blocks.FootNote && (
            <Typography
              component="p"
              variant="caption"
              sx={{ color: theme.palette.grey[600], fontSize: 16, mt: 2 }}
              align={alignment}
            >
              <GuaranteeIcon fontSize="large" sx={{ verticalAlign: '-0.5rem' }} /> {blocks.FootNote}
            </Typography>
          )}
        </Box>

        {ctaHeroMediaUrl && (
          <Box
            sx={{
              mx: 'auto',
              px: 2,
              maxWidth: {
                xs: '25rem',
                sm: '20rem',
                md: '22rem',
                lg: '25rem',
              },
            }}
          >
            {mediaElement}
          </Box>
        )}
      </Box>
    </CustomBackground>
  );
}
